import { useEffect, useRef, type RefObject } from "react";

import { normalizeFloorSerialInput } from "./floorSerialScan";
import { notifyLaborAttentionActivity } from "./labor/useLaborAttentionActivity";

/** Hub pages mark the floor serial field with this attribute so the tablet launcher can find it. */
export const SGS_SCAN_TARGET_ATTRIBUTE = "data-sgs-scan-target";

/** Well-known callback the tablet chrome Scan button invokes when present. */
export const SGS_PAGE_BARCODE_SCAN_CALLBACK = "__sgsOnBarcodeScan";

export const FLOOR_PAGE_SCAN_TARGET_PROPS = {
  [SGS_SCAN_TARGET_ATTRIBUTE]: "",
} as const;

type PageScanWindow = Window &
  Record<typeof SGS_PAGE_BARCODE_SCAN_CALLBACK, ((text: string) => void) | undefined> & {
    __sgsOnBarcodeScanStack?: Array<(text: string) => void>;
  };

export function isHidScanModifierBlocked(event: {
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
}): boolean {
  return event.ctrlKey || event.metaKey || event.altKey;
}

export function shouldStealHidScanFromEventTarget(target: EventTarget | null): boolean {
  if (target == null) {
    return true;
  }
  const tag = (target as { tagName?: string }).tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
    return false;
  }
  return (target as { isContentEditable?: boolean }).isContentEditable !== true;
}

export function applyHidScanKey(
  buffer: string,
  key: string,
): { buffer: string; submitted: string | null } {
  if (key === "Enter" || key === "Tab" || key === "\n" || key === "\r") {
    return { buffer: "", submitted: buffer.length > 0 ? buffer : null };
  }
  if (key === "Backspace") {
    return { buffer: buffer.slice(0, -1), submitted: null };
  }
  if (key.length !== 1) {
    return { buffer, submitted: null };
  }
  return { buffer: buffer + key, submitted: null };
}

export function resolveFloorScanInputs(
  inputRef?: RefObject<HTMLInputElement | null>,
  inputRefs?: ReadonlyArray<RefObject<HTMLInputElement | null>>,
): HTMLInputElement[] {
  const refs = [...(inputRefs ?? [])];
  if (inputRef != null) {
    refs.unshift(inputRef);
  }
  const seen = new Set<HTMLInputElement>();
  const resolved: HTMLInputElement[] = [];
  for (const ref of refs) {
    const element = ref.current;
    if (element != null && !seen.has(element)) {
      seen.add(element);
      resolved.push(element);
    }
  }
  return resolved;
}

/**
 * While enabled, camera scans and (optionally) unfocused Bluetooth HID keystrokes go to `onScan`.
 * Marked fields stay the page scan target for the tablet launcher.
 */
export function useFloorPageScanCapture(options: {
  enabled?: boolean;
  /** When false, camera still delivers to `onScan` but HID digits are left for numpads. Default true. */
  captureUnfocusedHid?: boolean;
  autoFocus?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  inputRefs?: ReadonlyArray<RefObject<HTMLInputElement | null>>;
  onScan: (serial: string) => void;
}): void {
  const {
    enabled = true,
    captureUnfocusedHid = true,
    autoFocus = true,
    inputRef,
    inputRefs,
    onScan,
  } = options;
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const hidBufferRef = useRef("");
  const inputRefHandle = inputRef ?? null;
  const extraRefsRef = useRef(inputRefs);
  extraRefsRef.current = inputRefs;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const deliver = (raw: string) => {
      const serial = normalizeFloorSerialInput(raw);
      if (serial === "") {
        return;
      }
      notifyLaborAttentionActivity();
      onScanRef.current(serial);
    };

    const inputs = resolveFloorScanInputs(inputRefHandle ?? undefined, extraRefsRef.current);
    for (const input of inputs) {
      input.setAttribute(SGS_SCAN_TARGET_ATTRIBUTE, "");
    }
    if (autoFocus) {
      inputs[0]?.focus();
    }

    const pageWindow = window as PageScanWindow;
    const deliverToPage = (text: string) => {
      deliver(text);
    };
    const stack = pageWindow.__sgsOnBarcodeScanStack ?? [];
    stack.push(deliverToPage);
    pageWindow.__sgsOnBarcodeScanStack = stack;
    pageWindow[SGS_PAGE_BARCODE_SCAN_CALLBACK] = (text: string) => {
      stack[stack.length - 1]?.(text);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const owned = pageWindow.__sgsOnBarcodeScanStack ?? [];
      if (owned[owned.length - 1] !== deliverToPage) {
        return;
      }
      if (!captureUnfocusedHid) {
        return;
      }
      if (!shouldStealHidScanFromEventTarget(event.target)) {
        return;
      }
      if (isHidScanModifierBlocked(event)) {
        return;
      }
      const next = applyHidScanKey(hidBufferRef.current, event.key);
      if (next.buffer === hidBufferRef.current && next.submitted == null) {
        return;
      }
      event.preventDefault();
      hidBufferRef.current = next.buffer;
      if (next.submitted != null) {
        deliver(next.submitted);
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      hidBufferRef.current = "";
      const owned = pageWindow.__sgsOnBarcodeScanStack ?? [];
      const index = owned.lastIndexOf(deliverToPage);
      if (index >= 0) {
        owned.splice(index, 1);
      }
      if (owned.length === 0) {
        delete pageWindow[SGS_PAGE_BARCODE_SCAN_CALLBACK];
        delete pageWindow.__sgsOnBarcodeScanStack;
      } else {
        pageWindow[SGS_PAGE_BARCODE_SCAN_CALLBACK] = (text: string) => {
          owned[owned.length - 1]?.(text);
        };
      }
      for (const input of resolveFloorScanInputs(inputRefHandle ?? undefined, extraRefsRef.current)) {
        input.removeAttribute(SGS_SCAN_TARGET_ATTRIBUTE);
      }
    };
  }, [autoFocus, captureUnfocusedHid, enabled, inputRefHandle]);
}
